import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import styles from "../../../styles.module.css";
import { useSelector } from "react-redux";
import {
  Breadcrumb,
  CustomPopover,
  DeleteDialog,
  StyledTableCell,
  StyledTableRow,
  HandleSort,
  CustomePopover,
} from "../../reusableComponent/Reusables";

import HeaderSection from "../../reusableComponent/HeaderSection";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

import grnService from "../../../services/grnService";
import rmMasterService from "../../../services/masters/rmMaster.Service";
import RmIssuedService from "../../../services/RmIssued.service";

const StockImport = () => {
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD?.userInfo;

  const [rows, setRows] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [isDelete, setIsDelete] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const totalCount = useSelector((state) => state.grn.total_count || 0);
  const defaultRowsPerPage = useSelector((state) => state.grn.rows_per_page || 15);
  const respMsgState = useSelector((state) => state.grn.message);
  const [total_count,setTotalCount]=useState(null)
  useEffect(() => {
    setTotalCount(rows[0]?.totalCount);
  }, [rows]);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: defaultRowsPerPage,
  });

  // Updated fields to match stockRows keys (Qty instead of qty)
  const fields = ["rm_name", "opening_balance", "accepted_qty", "Qty", "stock_balance"];
  const labels = ["RM Name", "Opening Balance", "Accepted Qty", "Issue Qty", "Stock Balance"];

const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    const [rmData, rmIssueData, grnData] = await Promise.all([
      rmMasterService.getRMList({ page: 0, size: 999, searchKey: "" }),
      RmIssuedService.getRmIssuedList({ page: 0, size: 999, searchKey: "" }),
      grnService.getGrnList({ page: 0, size: 999, searchKey: "" }),
    ]);

    // Check if data is empty
    if (!grnData?.length || !grnData[0]?.items?.length) {
      setSuccessMessage("No GRN data available");
    }
    if (!rmIssueData?.length || !rmIssueData[0]?.items?.length) {
      setSuccessMessage("No RM issue data available");
    }

    // Create rmMap with rm_code as key
    const rmMap = rmData.reduce((acc, item) => {
      acc[item.rm_code] = {
        rm_name: item.rm_name,
        opening_balance: parseFloat(item.opening_bal) || 0,
      };
      return acc;
    }, {});

    // Aggregate accepted_qty by item_code from grnData
    const grnQtyMap = grnData.reduce((acc, grn) => {
      let items = grn.items;
      if (typeof items === "string") {
        try {
          items = JSON.parse(items);
        } catch (err) {
          console.warn("Invalid JSON in GRN items:", grn.items);
          return acc;
        }
      }

      if (Array.isArray(items)) {
        items.forEach((item) => {
          const code = item.item_code; // Assuming item_code matches rm_code
          const qty = parseFloat(item.accepted_qty) || 0;
          if (code && !isNaN(qty)) {
            acc[code] = (acc[code] || 0) + qty; // Accumulate quantity
          } else {
            console.warn("Invalid GRN item:", item);
          }
        });
      }
      return acc;
    }, {});

    // Aggregate issued_qty by item_code from rmIssueData
    const rmIssueQtyMap = rmIssueData.reduce((acc, issue) => {
      let items = issue.items;
      if (typeof items === "string") {
        try {
          items = JSON.parse(items);
        } catch (err) {
          console.warn("Invalid JSON in RM issue items:", issue.items);
          return acc;
        }
      }

      if (Array.isArray(items)) {
        items.forEach((item) => {
          const code = item.item_code; // Assuming item_code matches rm_code
          const qty = parseFloat(item.qty) || 0;
          if (code && !isNaN(qty)) {
            acc[code] = (acc[code] || 0) + qty; // Accumulate quantity
          } else {
            console.warn("Invalid RM issue item:", item);
          }
        });
      }
      return acc;
    }, {});

    // Log maps for debugging
    console.log("rmMap:", rmMap);
    console.log("grnQtyMap:", grnQtyMap);
    console.log("rmIssueQtyMap:", rmIssueQtyMap);

    // Create stockRows
    const stockRows = Object.keys(rmMap).map((code) => {
      const opening = rmMap[code].opening_balance || 0;
      const accepted_qty = grnQtyMap[code] || 0;
      const issued_qty = rmIssueQtyMap[code] || 0;
      const stock_balance = opening + accepted_qty - issued_qty;

      // Debug log for each row
      console.log(
        `Code: ${code}, Opening: ${opening}, Accepted Qty: ${accepted_qty}, Issued Qty: ${issued_qty}, Stock Balance: ${stock_balance}`
      );

      return {
        rm_name: rmMap[code].rm_name,
        opening_balance: opening,
        accepted_qty,
        issued_qty, 
        stock_balance,
        entry_id: code,
      };
    });

    // Log final rows
    console.log("stockRows:", stockRows);
    setRows(stockRows);
  } catch (error) {
    console.error("Error fetching data", error);
    setSuccessMessage("Error fetching data");
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchKey, fetchData]); 

  useEffect(() => {
    if (respMsgState) {
      setSuccessMessage(respMsgState);
    }
  }, [respMsgState]);

  const handleChangePage = (event, newPage) => {
    setController((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const handleSearch = (e) => {
    setSearchKey(e.target.value);
  };

  const deleteRecord = async (id) => {
    try {
      await grnService.editDeleteGrn({
        entry_id: id,
        changedUpdatedValue: "delete",
      });
      setIsDelete(false);
      setSuccessMessage("Record deleted successfully");
      fetchData();
    } catch (error) {
      setSuccessMessage("Error deleting record");
    }
  };

  const handleDelete = (row) => {
    setIsDelete(true);
    setDeleteInfo(row);
  };

  const handleClose = () => {
    setIsDelete(false);
    setDeleteInfo(null);
  };

  const filteredRows = rows.filter((row) =>
    row.rm_name.toLowerCase().includes(searchKey.toLowerCase())
  );

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (sortBy) {
      const isDesc = sortBy.startsWith("-");
      const key = isDesc ? sortBy.slice(1) : sortBy;
      const aValue = a[key];
      const bValue = b[key];
      if (typeof aValue === "string") {
        return isDesc
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }
      return isDesc ? bValue - aValue : aValue - bValue;
    }
    return 0;
  });

  const effectiveTotalCount = total_count > 0 ? total_count : rows.length > 0 ? rows.length : 0;
  const startRecord = effectiveTotalCount === 0 ? 0 : controller.page * controller.rowsPerPage + 1;
  const endRecord = effectiveTotalCount === 0 ? 0 : Math.min((controller.page + 1) * controller.rowsPerPage, effectiveTotalCount);
  const paginationInfo = `${startRecord}-${endRecord} of ${total_count}`;


  return (
    <Box className={styles.cts_page} sx={{ padding: "0px", margin: "0px" }}>
      <Box className={styles.body}>
        <Breadcrumb routeSegments={[{ name: "Stock Report" }]} />

        <HeaderSection
          title="Stock Report"
          searchKey={searchKey}
          handleSearch={handleSearch}
          sortedRows={sortedRows}
          fields={fields}
          labels={labels}
          filename="Stock Report"
          paginationInfo={paginationInfo}
          controller={controller}
          handleChangePage={handleChangePage}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: "100%" }}>
            <TableContainer component={Paper} className={styles.list_tbl_container}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell align="left" className={styles.table_head}>
                      Entry No
                    </StyledTableCell>
                    {fields.map((field, index) => (
                      <StyledTableCell
                        key={field}
                        align="left"
                        className={styles.table_head}
                        onClick={() =>
                          HandleSort(field, sortBy, setSortBy, rows)
                        }
                      >
                        {labels[index]}
                        <span className={styles.sort_icon}>
                          {sortBy === field
                            ? "▲"
                            : sortBy === `-${field}`
                            ? "▼"
                            : "⇅"}
                        </span>
                      </StyledTableCell>
                    ))}
                    <StyledTableCell align="center" className={styles.table_head}>
                      Action
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: "#fff" }}>
                  {sortedRows
                    .slice(
                      controller.page * controller.rowsPerPage,
                      controller.page * controller.rowsPerPage + controller.rowsPerPage
                    )
                    .map((row, index) => (
                      <StyledTableRow key={`${row.rm_name}-${index}`}>
                        <StyledTableCell align="left" className={styles.list_table_body}>
                          {controller.page * controller.rowsPerPage + index + 1}
                        </StyledTableCell>
                        <StyledTableCell align="left" className={styles.list_table_body}>
                          {row.rm_name}
                        </StyledTableCell>
                        <StyledTableCell align="left" className={styles.list_table_body}>
                          {row.opening_balance}
                        </StyledTableCell>
                        <StyledTableCell align="left" className={styles.list_table_body}>
                          {row.accepted_qty}
                        </StyledTableCell>
                        <StyledTableCell align="left" className={styles.list_table_body}>
                          {row.issued_qty}
                        </StyledTableCell>
                        <StyledTableCell align="left" className={styles.list_table_body}>
                          {row.stock_balance}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <CustomePopover row={row} handleDelete={false} />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>


            <TablePagination
              rowsPerPageOptions={[5, 10, 15, 25]}
              component="div"
              count={effectiveTotalCount}
              rowsPerPage={controller.rowsPerPage}
              page={controller.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />


           </Paper>
        )}

        <DeleteDialog
          open={isDelete}
          handleClose={handleClose}
          deleteInfo={deleteInfo}
          deleteRecord={deleteRecord}
          deleteID={deleteInfo?.entry_id || null}
          displayName={deleteInfo?.rm_name || ""}
        />
        <ReusableSnackbar
          open={!!successMessage}
          onClose={() => setSuccessMessage("")}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
        />
      </Box>
    </Box>
  );
};

export default StockImport;